import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Brain, Mic, PenLine, Paperclip, ScanLine, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAIStore } from '@/stores/useAIStore';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { useChartActionsStore } from '@/stores/useChartActionsStore';
import { useAiAffordance } from '@/config/aiCapabilities';
import { usePlatformFlag } from '@/config/platformFlags';
import { useFloatingClearance } from '@/hooks/useFloatingClearance';
import { Brand } from '@/components/kit';
import { useMaieShell } from '@/config/maieShell';

/**
 * MAIE launcher — a single circular FAB at bottom-right. Tap it to fan out a
 * speed-dial: capture actions (Record / Write note / Scan / Add file) plus
 * Ask MAIE (the AI chat, set apart at the top — different mode). Patient-chart
 * items (Write note / Add file) only appear while a chart is mounted, wired
 * through `useChartActionsStore`. When only one action is available the FAB
 * skips the dial and does it directly.
 *
 * Hidden while an encounter is capturing/paused (the RecordingDock owns the
 * corner) and while the chat panel is open.
 *
 * The gradient stays on the primary ramp (600→700) so white text/icons keep
 * legible contrast across every (tenant-configurable) theme — fading to
 * `--color-accent` could land on a light hue.
 */
const FAB_GRADIENT =
  'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))';

// One-time coach mark: the bare mic circle reads as decoration to new
// users — they finish the demo and don't know where real encounters get
// recorded. Label it until they use it once (per device).
const RECORD_HINT_KEY = 'maie.recordFabHintDismissed';

type DialItem = {
  key: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
};

export const MaieLauncherPill: React.FC = () => {
  /* The design shell draws the brand mark in this circle; the legacy shell
     keeps the tool icon it has always had. */
  const onDesignShell = useMaieShell();
  const { isRTL } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const setShowAssistant = useAIStore((s) => s.setShowAssistant);
  const showAssistant = useAIStore((s) => s.showAssistant);

  const session = useRecordingSessionStore((s) => s.session);
  const armSession = useRecordingSessionStore((s) => s.armSession);
  const abandon = useRecordingSessionStore((s) => s.abandon);

  const chart = useChartActionsStore((s) => s.actions);
  const scanEnabled = usePlatformFlag('AI_OCR_INBOX_ENABLED');

  const scribe = useAiAffordance('scribe');
  const assistant = useAiAffordance('assistant');
  const scribeManualFallback = !scribe.enabled;

  const { ref: fabRef, lift: bottomLift } =
    useFloatingClearance<HTMLButtonElement>();
  const [open, setOpen] = React.useState(false);

  const [recordHintDismissed, setRecordHintDismissed] = React.useState(() => {
    try {
      return localStorage.getItem(RECORD_HINT_KEY) === '1';
    } catch {
      return true;
    }
  });
  const dismissRecordHint = React.useCallback(() => {
    setRecordHintDismissed(true);
    try {
      localStorage.setItem(RECORD_HINT_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const dockOwnsCorner =
    !!session && (session.status === 'capturing' || session.status === 'paused');

  React.useEffect(() => {
    if (dockOwnsCorner || showAssistant) setOpen(false);
  }, [dockOwnsCorner, showAssistant]);

  // Any session existing means they found a record entry point — the
  // hint has served its purpose.
  React.useEffect(() => {
    if (session && !recordHintDismissed) dismissRecordHint();
  }, [session, recordHintDismissed, dismissRecordHint]);

  const handleStartEncounter = () => {
    if (scribeManualFallback) {
      if (scribe.message) toast.info(scribe.message);
      navigate('/encounter/new/manual');
      return;
    }
    if (!session) {
      armSession({});
      return;
    }
    if (session.status === 'stopped') {
      void (async () => {
        await abandon();
        armSession({});
      })();
    }
  };

  // Capture group — Record sits at the bottom, nearest the thumb.
  const captureItems: DialItem[] = [];
  if (chart) {
    captureItems.push({
      key: 'file',
      label: isRTL ? 'إرفاق ملف' : 'Add file',
      Icon: Paperclip,
      onClick: chart.onAddFile,
    });
  }
  if (scanEnabled) {
    captureItems.push({
      key: 'scan',
      label: isRTL ? 'مسح مستند' : 'Scan document',
      Icon: ScanLine,
      onClick: () => navigate('/scan'),
    });
  }
  if (chart) {
    captureItems.push({
      key: 'note',
      label: isRTL ? 'كتابة ملاحظة' : 'Write note',
      Icon: PenLine,
      onClick: chart.onWriteNote,
    });
  }
  captureItems.push({
    key: 'record',
    label: scribeManualFallback
      ? isRTL ? 'سجل يدوي' : 'Manual note'
      : isRTL ? 'تسجيل' : 'Record',
    Icon: scribeManualFallback ? PenLine : Mic,
    onClick: handleStartEncounter,
    primary: true,
  });

  // Ask MAIE — set apart at the top of the dial (chat, not capture).
  const askItem: DialItem | null = assistant.show
    ? {
        key: 'ask',
        label: isRTL ? 'اسأل MAIE' : 'Ask MAIE',
        Icon: Brain,
        // Keep it tappable even when not entitled (quota_exceeded): opening the
        // bubble is how the doctor reaches the subscribe prompt. Only a genuine
        // outage disables it.
        onClick: () => setShowAssistant(true),
        disabled: !assistant.enabled && assistant.reason === 'unavailable',
      }
    : null;

  // Single available action → no dial, act directly (keeps record one tap when
  // there's nothing else to choose).
  const allItems = [...(askItem ? [askItem] : []), ...captureItems];
  const hasDial = allItems.length > 1;

  if (dockOwnsCorner) return null;
  if (showAssistant) return null;
  if (pathname.startsWith('/inbox')) return null;

  const handleFabClick = () => {
    dismissRecordHint();
    if (!hasDial) {
      captureItems[captureItems.length - 1].onClick();
      return;
    }
    setOpen((o) => !o);
  };

  const runItem = (item: DialItem) => {
    if (item.disabled) return;
    setOpen(false);
    item.onClick();
  };

  const FabIcon = open ? X : scribeManualFallback ? PenLine : Mic;

  return (
    <>
      {open && hasDial && (
        <button
          type="button"
          aria-label={isRTL ? 'إغلاق' : 'Close'}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30"
          style={{
            backgroundColor: 'rgba(4,5,9,0.45)',
            backdropFilter: 'blur(1px)',
            animation: 'maieDialScrim 0.22s ease-out both',
          }}
        />
      )}

      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="maie-launcher-dock fixed bottom-6 end-6 z-40 flex flex-col items-end gap-3"
        style={{
          marginBottom: 'env(safe-area-inset-bottom, 0)',
          transform: bottomLift > 0 ? `translateY(-${bottomLift}px)` : undefined,
        }}
      >
        {open && hasDial && (
          <div className="flex flex-col items-end gap-3 mb-1">
            {askItem && (
              <>
                <DialRow
                  item={askItem}
                  delaySec={allItems.length * 0.045}
                  isRTL={isRTL}
                  onRun={runItem}
                />
                {/* divider between chat and capture actions */}
                <span
                  aria-hidden
                  className="h-px w-10 self-end my-0.5"
                  style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                />
              </>
            )}
            {captureItems.map((item, i) => (
              <DialRow
                key={item.key}
                item={item}
                // Record (last) animates in first.
                delaySec={(captureItems.length - 1 - i) * 0.045}
                isRTL={isRTL}
                onRun={runItem}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* One-time "this is where you record" label. Goes away for
              good the first time the FAB is used (or any recording
              starts anywhere). Skipped for manual-fallback tenants —
              nothing voice-shaped to point at. */}
          {!open && !recordHintDismissed && !scribeManualFallback && (
            <button
              type="button"
              onClick={handleFabClick}
              className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap select-none"
              style={{
                background: FAB_GRADIENT,
                color: '#fff',
                boxShadow: 'var(--shadow-lg)',
                animation: 'maieDialIn 0.3s ease-out both',
              }}
            >
              {isRTL ? 'سجّل الزيارات من هنا' : 'Record visits here'}
            </button>
          )}
          <button
            ref={fabRef}
            type="button"
            onClick={handleFabClick}
            aria-haspopup={hasDial ? 'menu' : undefined}
            aria-expanded={hasDial ? open : undefined}
            aria-label={
              open ? (isRTL ? 'إغلاق' : 'Close') : isRTL ? 'إجراءات' : 'Actions'
            }
            className="w-14 h-14 rounded-full grid place-items-center select-none"
            style={{
              background: FAB_GRADIENT,
              color: '#fff',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            {/* THE BOARDS DRAW THE MARK, NOT A TOOL. Every canvas puts the
                MAIE symbol in this circle: the launcher is the assistant, and
                which tool it opens with (a mic, or a pen where the clinic has
                no scribe) is what the dial says, not what the button is. The X
                still wins while the dial is open — then the button's job IS to
                close it. */}
            {open || !onDesignShell ? (
              <FabIcon className="w-6 h-6" />
            ) : (
              <Brand mark="symbol" style={{ width: 26, height: 26 }} />
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes maieDialIn {
          from { opacity: 0; transform: translateY(14px) scale(0.82); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes maieDialScrim {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
};

const DialRow: React.FC<{
  item: DialItem;
  delaySec: number;
  isRTL: boolean;
  onRun: (item: DialItem) => void;
}> = ({ item, delaySec, onRun }) => (
  <button
    type="button"
    onClick={() => onRun(item)}
    disabled={item.disabled}
    className={`flex items-center gap-2.5 ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    style={{
      animation: 'maieDialIn 0.34s cubic-bezier(0.16,1,0.3,1) both',
      animationDelay: `${delaySec}s`,
    }}
  >
    <span
      className="text-sm font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
        border: '1px solid var(--color-border-primary)',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      {item.label}
    </span>
    <span
      className="w-12 h-12 rounded-full grid place-items-center flex-shrink-0"
      style={
        item.primary
          ? {
              background: FAB_GRADIENT,
              color: '#fff',
              boxShadow: 'var(--shadow-xl)',
            }
          : {
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-primary-700)',
              border: '1px solid var(--color-border-primary)',
              boxShadow: 'var(--shadow-lg)',
            }
      }
    >
      <item.Icon className="w-5 h-5" />
    </span>
  </button>
);

export default MaieLauncherPill;
